import { FC, ReactNode } from 'react';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { get } from 'lodash';
import { TimeInput, TimeUnit } from './TimeInput';
import { toTimeInputOutputUnit } from '@api/models/enums/TimeInputOutputUnit';

type ToggleType = 'off' | 'on';
const INITIAL_VALUE = 1;

interface ConditionalTimeInputProps {
  fieldName: string;
  titleKey: string;
  timeInputLabelKey: string;
  defaultValue?: number;
  outputUnit?: 'minutes' | 'hours' | 'days' | 'months';
  defaultUnit?: 'minutes' | 'hours' | 'days' | 'months';
  outputFormat?: 'number' | 'object';
  /** Locks the unit selection to a single unit. */
  forcedUnit?: TimeUnit;
  /** Further settings shown below the time input while the toggle is on. */
  children?: ReactNode;
  /** Called after the toggle changes, e.g. to seed or clear related fields. */
  onToggle?: (enabled: boolean) => void;
}

export const ConditionalTimeInput: FC<ConditionalTimeInputProps> = ({
  fieldName,
  titleKey,
  timeInputLabelKey,
  outputUnit = 'days',
  defaultUnit = 'months',
  outputFormat,
  forcedUnit,
  children,
  onToggle
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<Record<string, unknown>>();

  const isEnabled = get(values, fieldName) != null;
  const toggleValue: ToggleType = isEnabled ? 'on' : 'off';

  const handleToggleChange = (_event: React.MouseEvent<HTMLElement>, newValue: ToggleType | null) => {
    if (newValue == null) return;

    if (newValue === 'off') {
      setFieldValue(fieldName, null);
    } else {
      setFieldValue(
        fieldName,
        outputFormat === 'number' ? INITIAL_VALUE : { value: INITIAL_VALUE, unit: toTimeInputOutputUnit(forcedUnit ?? defaultUnit) },
      );
    }

    onToggle?.(newValue === 'on');
  };

  return (
    <Box mb={1.5}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 1,
        }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          {t(titleKey)}
        </Typography>

        <ToggleButtonGroup
          value={toggleValue}
          exclusive
          onChange={handleToggleChange}
          size="small"
          sx={{ flexShrink: 0 }}
        >
          <ToggleButton value="off" sx={{ textTransform: 'none', px: 2 }}>
            {t('conditionalTimeInput.off')}
          </ToggleButton>
          <ToggleButton value="on" sx={{ textTransform: 'none', px: 2 }}>
            {t('conditionalTimeInput.on')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {isEnabled && (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Box sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: '300px' }, marginTop: { xs: 2, sm: 0 } }}>
              <TimeInput
                outputUnit={outputUnit}
                defaultUnit={defaultUnit}
                forcedUnit={forcedUnit}
                name={fieldName}
                label={t(timeInputLabelKey)}
                outputFormat={outputFormat}
              />
            </Box>
          </Box>

          {children && <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>{children}</Box>}
        </>
      )}
    </Box>
  );
};
